import React, { useCallback, useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import {
  ArrowLeft,
  Edit,
  ToggleLeft,
  ToggleRight,
  Users,
  UserPlus,
  Calendar,
  Target,
  Activity,
  User,
  Settings,
  Info,
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  TrendingUp,
  Handshake,
  Gift,
  Star,
  Copy,
  Share2,
  BarChart3,
} from "lucide-react";
import {
  useReferralCampaign,
  useToggleReferralCampaignStatus,
  formatRewardDisplay,
  getRewardTypeDisplay,
} from "@/api-services/hooks/useReferralCampaigns";
import {
  ReferralCampaign,
  REWARD_TYPES,
} from "@/api-services/hooks/referralCampaignTypes";

const ViewReferralCampaignPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const referralCampaignId = parseInt(id || "0");

  // State for managing toggle confirmation
  const [isToggling, setIsToggling] = useState(false);

  // Fetch referral campaign details
  const { data: referralCampaign, loading, error, refetch } = useReferralCampaign(
    referralCampaignId,
    {
      enabled: !!referralCampaignId && referralCampaignId > 0,
      onError: (err) => {
        toast.error(`Failed to load referral campaign details: ${err.message}`);
      },
    }
  );

  // Toggle referral campaign status hook
  const { mutate: toggleStatus } = useToggleReferralCampaignStatus({
    onSuccess: (data, referralCampaignId) => {
      setIsToggling(false);
      toast.success("Referral campaign status updated successfully!");
      refetch(); // Refresh the data
    },
    onError: (error, referralCampaignId) => {
      setIsToggling(false);
      toast.error(`Failed to update referral campaign status: ${error.message}`);
    },
  });

  // Format date
  const formatDate = useCallback((dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }, []);

  // Format date with time
  const formatDateTime = useCallback((dateString?: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString();
  }, []);

  // Get status badge
  const getStatusBadge = useCallback((isActive: boolean) => {
    return isActive
      ? {
          variant: "default" as const,
          className: "bg-success text-success-foreground",
          label: "Active",
          icon: CheckCircle
        }
      : {
          variant: "secondary" as const,
          className: "bg-muted text-muted-foreground",
          label: "Inactive",
          icon: XCircle
        };
  }, []);

  // Handle status toggle
  const handleToggleStatus = () => {
    if (!referralCampaign) return;

    setIsToggling(true);
    toggleStatus(referralCampaign.referralCampaignId);
  };

  // Handle navigation
  const handleEdit = () => {
    navigate(`/admin/referral-campaigns/edit/${referralCampaignId}`);
  };

  const handleBack = () => {
    navigate("/admin/referral-campaigns");
  };

  // Copy campaign ID to clipboard
  const copyCampaignId = useCallback(async (id: number) => {
    try {
      // Validate the campaign ID
      if (!id || typeof id !== 'number') {
        toast.error("No campaign ID available to copy");
        return;
      }

      const idString = id.toString();

      // Fallback for browsers that don't support navigator.clipboard
      if (!navigator.clipboard) {
        // Create a temporary textarea element
        const textArea = document.createElement("textarea");
        textArea.value = idString;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);

          if (successful) {
            toast.success("Campaign ID copied to clipboard!");
          } else {
            toast.error("Failed to copy campaign ID");
          }
        } catch (fallbackErr) {
          document.body.removeChild(textArea);
          toast.error("Failed to copy campaign ID");
        }
        return;
      }

      // Modern browsers with clipboard API support
      await navigator.clipboard.writeText(idString);
      toast.success("Campaign ID copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy campaign ID");
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading referral campaign details...</p>
        </div>
      </div>
    );
  }

  if (error || !referralCampaign) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">
              Error Loading Referral Campaign
            </h3>
            <p className="text-muted-foreground mb-4">
              {error?.message || "Referral campaign not found"}
            </p>
            <div className="flex gap-2 justify-center">
              <Button variant="outline" onClick={() => refetch()}>
                Try Again
              </Button>
              <Button onClick={handleBack}>Back to List</Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const statusBadge = getStatusBadge(referralCampaign.isActive);
  const StatusIcon = statusBadge.icon;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="sm" onClick={handleBack}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Referral Campaigns
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-foreground">
              Referral Campaign Details
            </h1>
            <p className="text-muted-foreground mt-1">
              View detailed information about this referral campaign
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleToggleStatus}
            disabled={isToggling}
          >
            {referralCampaign.isActive ? (
              <>
                <ToggleLeft className="w-4 h-4 mr-2" />
                Deactivate
              </>
            ) : (
              <>
                <ToggleRight className="w-4 h-4 mr-2" />
                Activate
              </>
            )}
          </Button>
          <Button onClick={handleEdit}>
            <Edit className="w-4 h-4 mr-2" />
            Edit Campaign
          </Button>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Information */}
        <div className="lg:col-span-2 space-y-6">
          {/* Campaign ID Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Handshake className="w-5 h-5" />
                Campaign Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Campaign ID */}
              <div className="space-y-2">
                <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                  <Target className="w-4 h-4" />
                  Campaign ID
                </label>
                <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-2xl font-bold text-blue-700">
                      #{referralCampaign.referralCampaignId}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => copyCampaignId(referralCampaign?.referralCampaignId || 0)}
                    className="border-blue-300 text-blue-700 hover:bg-blue-100"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Reward Details Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gift className="w-5 h-5" />
                Reward Structure
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Referrer Rewards */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Referrer Reward Type
                    </label>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="font-semibold">
                        {getRewardTypeDisplay(referralCampaign.referrerRewardType)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">
                      Referrer Reward Value
                    </label>
                    <div className="p-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg">
                      <p className="text-xl font-bold text-green-700">
                        {formatRewardDisplay(referralCampaign.referrerRewardType, referralCampaign.referrerRewardValue)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Referred User Rewards */}
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Referred User Reward Type
                    </label>
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <p className="font-semibold">
                        {getRewardTypeDisplay(referralCampaign.referredRewardType)}
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-muted-foreground">
                      Referred User Reward Value
                    </label>
                    <div className="p-3 bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-lg">
                      <p className="text-xl font-bold text-blue-700">
                        {formatRewardDisplay(referralCampaign.referredRewardType, referralCampaign.referredRewardValue)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Campaign Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="w-5 h-5" />
                Campaign Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Validity Period */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Validity Period
                  </label>
                  <div className="p-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg">
                    <p className="text-xl font-bold text-purple-700">
                      {referralCampaign.validityDays} days
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Rewards remain valid for this many days
                    </p>
                  </div>
                </div>

                {/* Usage Limits */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Usage Limits
                  </label>
                  <div className="p-3 bg-gradient-to-r from-orange-50 to-red-50 border border-orange-200 rounded-lg">
                    <p className="text-xl font-bold text-orange-700">
                      {referralCampaign.maxUsesPerReferrer}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Maximum uses per referrer
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* System Information Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                System Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Created By
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="font-medium">
                      {referralCampaign.createdBy || "Unknown"}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Campaign Status
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <Badge
                      className={statusBadge.className}
                    >
                      <StatusIcon className="w-3 h-3 mr-1" />
                      {statusBadge.label}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Created Date
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-sm">
                      {formatDateTime(referralCampaign.createdDate)}
                    </p>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground">
                    Last Modified
                  </label>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-sm">
                      {formatDateTime(referralCampaign.modifiedDate)}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Status & Actions */}
        <div className="space-y-6">
          {/* Status Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Campaign Status
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Handshake className="w-8 h-8 text-white" />
                </div>
                <Badge
                  variant={statusBadge.variant}
                  className={`${statusBadge.className} text-sm px-3 py-1`}
                >
                  <StatusIcon className="w-3 h-3 mr-1" />
                  {statusBadge.label}
                </Badge>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Campaign ID:</span>
                  <span className="font-mono">#{referralCampaign.referralCampaignId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Deleted:</span>
                  <span>{referralCampaign.isDeletedValue ? "Yes" : "No"}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Stats Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5" />
                Campaign Stats
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">Validity Period</p>
                  <p className="font-semibold text-purple-600">
                    {referralCampaign.validityDays} days
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-1">Usage Limit</p>
                  <p className="font-semibold text-orange-600">
                    {referralCampaign.maxUsesPerReferrer} per referrer
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Quick Actions Card */}
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button
                onClick={handleEdit}
                className="w-full"
              >
                <Edit className="w-4 h-4 mr-2" />
                Edit Campaign
              </Button>
              <Button
                variant="outline"
                onClick={handleToggleStatus}
                disabled={isToggling}
                className="w-full"
              >
                {referralCampaign.isActive ? (
                  <>
                    <ToggleLeft className="w-4 h-4 mr-2" />
                    Deactivate Campaign
                  </>
                ) : (
                  <>
                    <ToggleRight className="w-4 h-4 mr-2" />
                    Activate Campaign
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => copyCampaignId(referralCampaign?.referralCampaignId || 0)}
                className="w-full"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Campaign ID
              </Button>
              <Button
                variant="outline"
                onClick={handleBack}
                className="w-full"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to List
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ViewReferralCampaignPage;